import {
    getFilterElementsDefault,
    Input,
    useWorkplaceState,
    WorkplaceBtnActions,
    WorkplaceBtnDelete
} from '@finbeat/react-toolkit';
import { faBoltLightning, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addTariff,
    deleteTariff,
    getTariffs,
    getTariffsExcel,
    getTariffsItem,
    updateTariff
} from 'axios/tariffsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    TariffAdd,
    TariffsFilter,
    TariffsInfo,
    TariffsTable,
    TariffUpdate
} from 'models/api/tariff';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import TariffsModal from './Modal/TariffsModal';
import './Tariffs.scss';
import TariffsAddButton from './TariffsAddButton';
import Filter from './TariffsFilter';

const Tariffs = () => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isModalVisible, setModalVisible] = useState(false);

    const queryKey = ['tariff'];

    return (
        <BOWorkplace<TariffsTable, TariffsInfo, TariffsFilter, TariffUpdate, TariffAdd>
            filterParams={{
                id: '',
                name: '',
            }}
            getFilterElements={getFilterElementsDefault([
                { name: t('BackOfficeBrandName'), id: 'id' },
                { name: t('BackOfficeTranslateGroup'), id: 'name' },
            ])}
            fetchExcelQueryFn={(filter) => getTariffsExcel(api, filter)}
            fetchQueryFn={(filter) => getTariffs(api, filter)}
            queryKey={queryKey}
            queryClient={queryClient}
            pageTitle="BackOfficeTariffs"
            storagePath="tariff/columns"
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey={'BackOfficeId'} />,
                },
                {
                    key: 'isActive',
                    name: <FontAwesomeIcon icon={faEyeSlash} />,
                    cellClass: 'base-data-grid__cell_centered',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    notHidden: true,
                    editable: true,
                    formatter: ({ row }) => (
                        <Input
                            type="checkbox"
                            className="tariffs__is-active-cell"
                            name="notUsed"
                            checked={row.isActive}
                            disabled
                        />
                    ),
                },
                { key: 'name', name: <Trans i18nKey={'BackOfficeName'} /> },
                { key: 'apr', name: <Trans i18nKey={'BackOfficeInterestRate'} /> },
                { key: 'minTerm', name: <Trans i18nKey={'BackOfficeMinTerm'} /> },
                { key: 'maxTerm', name: <Trans i18nKey={'BackOfficeMaxTerm'} /> },
                { key: 'termUnits', name: <Trans i18nKey={'BackOfficeTermUnits'} /> },
                { key: 'dsc', name: <Trans i18nKey={'BackOfficeDescription'} /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'base-data-grid__cell_centered no-change-modal',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 70,
                    minWidth: 70,
                    resizable: false,
                    clickDisable: true,
                    visible: hasRole('tariffsDelete'),
                    formatter: ({ row }) => (
                        <WorkplaceBtnActions>
                            <WorkplaceBtnDelete key={`delete-btn-${row.id}`} id={row.id} />
                        </WorkplaceBtnActions>
                    ),
                },
            ]}
            filter={<Filter />}
            showModal={hasRole('tariffsGetById') ? () => setModalVisible(true) : undefined}
            modal={{
                component: isModalVisible && (
                    <TariffsModal
                        queryKey={queryKey}
                        isOpen={isModalVisible}
                        toggle={() => setModalVisible(false)}
                    />
                ),
                fetchCurrentItemQueryFn: (item) => getTariffsItem(api, item),
                changeMutationFn: (item) => updateTariff(api, item),
                addMutationFn: (item) => addTariff(api, item),
            }}
            deleteMutationFn={(item) => deleteTariff(api, item)}
            buttons={
                hasRole('tariffsAdd')
                    ? [<TariffsAddButton key="1" showModal={() => setModalVisible(true)} />]
                    : []
            }
            showExcelBtn={hasRole('tariffsExcel')}
        />
    );
};

const useTariffsWorkplaceState = () =>
    useWorkplaceState<TariffsTable, TariffsInfo, TariffUpdate, TariffAdd>();

export default Tariffs;
export { useTariffsWorkplaceState };
